import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { SEOHelmet } from 'components/SEO';
import type { AppDispatch } from 'store';
import { meApi } from 'store/services/user/meApi';
import { playApi } from 'store/services/play/playApi';
import { loginSuccess } from 'store/services/auth/authSlice';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { getUserProperties, trackEvent, trackSignupConversion } from 'utils/analytics';
import { clearAttribution } from 'utils/attribution';
import { REFERRAL_SOURCE_STORAGE_KEY } from 'utils/referralSource';
import { beginDiscordLogin, safePlayReturnTo } from 'utils/play/discordAuth';
import { nextAfterSignup } from 'utils/play/signupRouting';
import { DISCORD_JOIN_UNAVAILABLE, PLAY_ERROR_MESSAGES } from 'utils/play/playErrors';
import { PlayLayout } from '../../layout/PlayLayout';
import '../Invite/InvitePage.css';

type Phase = 'working' | 'error' | 'failed';

/** No retry for these: the account itself is the problem; point to support (review ruling). */
const TERMINAL_ERRORS = ['BANNED', 'ACCOUNT_DELETED'];
/** Retrying Discord cannot help for these; the TradeMagic entry stays. */
const NO_DISCORD_RETRY = ['DISCORD_EMAIL_REQUIRED', 'DISCORD_NOT_CONFIGURED'];
const SUPPORT_EMAIL = 'support@trademagic.gg';

/**
 * Landing route for both Discord flows (Decision 4, ruling E10):
 * - sign-in: the backend callback set the auth cookies and 302'd here with
 *   ?new=0|1&returnTo=. A cookie session is invisible to the app until
 *   loginSuccess populates SecureStorage, so this route reads /users/me,
 *   dispatches loginSuccess, fires the signup conversion once on new=1 (E3),
 *   then navigates to returnTo.
 * - server join: ?joined=1|0&returnTo= for the Discord DM opt-in. The user is
 *   already signed in; refetch the Play notification prefs and go back.
 * - ?error=CODE renders the message with the two invite-page entries again.
 */
const DiscordCompletePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [phase, setPhase] = useState<Phase>('working');
  const ran = useRef(false);

  // Pinned on first render: the error and failed phases clear the stored copy
  // (Asana 1220218671326301, Decision 2) and the retry entries must keep their target.
  const [returnTo] = useState(() => safePlayReturnTo(params.get('returnTo') ?? ImportSessionStorage.getReturnTo(), '/play'));
  const errorCode = params.get('error');
  const joinedParam = params.get('joined');
  const isNew = params.get('new') === '1';
  const inviteReturn = /^\/play\/[a-z2-9]{6}(\/|$)/.test(returnTo) ? returnTo : null;

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (errorCode) {
      // A failed attempt leaves no return address behind; the retry entries store it again on click.
      ImportSessionStorage.clearReturnTo();
      setPhase('error');
      return;
    }

    if (joinedParam !== null) {
      // Server-join flow: the backend already wrote discordDm and discordGuildMember.
      dispatch(playApi.util.invalidateTags(['PlayNotifications']));
      ImportSessionStorage.getAndClearReturnTo();
      navigate(returnTo, { replace: true });
      return;
    }

    const run = async () => {
      try {
        const me = await dispatch(meApi.endpoints.getMe.initiate(undefined, { forceRefetch: true })).unwrap();
        dispatch(loginSuccess({ token: '', user: { id: me.id, username: me.username, roles: me.roles } }));
        dispatch(meApi.util.invalidateTags(['User']));
        if (isNew) {
          trackEvent('signup_complete', { ...getUserProperties({ id: me.id, createdAt: me.createdAt, country: me.country }), method: 'discord', source: 'play' });
          try {
            trackSignupConversion(me.email);
          } catch (conversionErr) {
            console.error('Signup conversion tracking failed (non-fatal):', conversionErr);
          }
          try {
            localStorage.removeItem(REFERRAL_SOURCE_STORAGE_KEY);
          } catch {
            // localStorage unavailable
          }
          clearAttribution();
        }
        ImportSessionStorage.getAndClearReturnTo();
        // A new Discord account from /play/host goes through the host username
        // step like an email sign-up (ruling 31); an invite returnTo already
        // lands on the invite name step.
        const next = isNew ? nextAfterSignup({ onboardingLevel: 'PLAY', returnTo }) : null;
        navigate(next && next.kind === 'play' ? next.to : returnTo, { replace: true });
      } catch {
        ImportSessionStorage.clearReturnTo();
        setPhase('failed');
      }
    };
    void run();
  }, [dispatch, navigate, returnTo, errorCode, joinedParam, isNew]);

  const message = errorCode ? PLAY_ERROR_MESSAGES[errorCode] ?? (joinedParam !== null ? DISCORD_JOIN_UNAVAILABLE : 'Discord sign-in did not complete.') : null;

  return (
    <PlayLayout>
      <SEOHelmet title="Signing you in - TradeMagic Play" description="Completing Discord sign-in for TradeMagic Play." noindex />
      <section className="play-card" data-testid="discord-complete" aria-busy={phase === 'working'}>
        {phase === 'working' && (
          <>
            <p className="play-eyebrow">Discord</p>
            <h1 className="play-title">Signing you in…</h1>
            <p className="play-helper">One moment while we finish up.</p>
          </>
        )}
        {phase === 'error' && (
          <>
            <p className="play-eyebrow">Discord</p>
            <h1 className="play-title">{joinedParam !== null ? "Couldn't connect to Discord" : 'Sign-in did not complete'}</h1>
            <p className="play-helper" role="alert" data-testid="discord-complete-error">
              {message}
            </p>
            {joinedParam === null && !TERMINAL_ERRORS.includes(errorCode ?? '') && (
              <div className="play-stack" style={{ marginTop: 16 }}>
                {!NO_DISCORD_RETRY.includes(errorCode ?? '') && (
                  <button type="button" className="play-discord-button" onClick={() => void beginDiscordLogin(returnTo)}>
                    <FontAwesomeIcon icon={faDiscord as IconProp} />
                    Continue with Discord
                  </button>
                )}
                <Link
                  to="/welcome?tab=signin"
                  className="play-secondary-button"
                  onClick={() => ImportSessionStorage.setReturnTo(returnTo)}
                >
                  I have a TradeMagic account
                </Link>
                {inviteReturn && (
                  <Link to={inviteReturn} className="play-text-button" style={{ textAlign: 'center', display: 'block' }}>
                    Back to the invite
                  </Link>
                )}
              </div>
            )}
            {joinedParam === null && TERMINAL_ERRORS.includes(errorCode ?? '') && (
              <p className="play-helper" style={{ marginTop: 12 }} data-testid="discord-complete-support">
                Think this is a mistake? Email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="play-text-button" style={{ padding: 0 }}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            )}
            {joinedParam !== null && (
              <p style={{ marginTop: 16 }}>
                <Link to={returnTo} className="play-secondary-button">
                  Back to alerts
                </Link>
              </p>
            )}
          </>
        )}
        {phase === 'failed' && (
          <>
            <p className="play-eyebrow">Discord</p>
            <h1 className="play-title">Sign-in didn't complete</h1>
            <p className="play-helper" role="alert">
              We couldn't confirm your session. Try again from the invite page.
            </p>
            <p style={{ marginTop: 16 }}>
              <Link to={inviteReturn ?? '/play'} className="play-secondary-button">
                {inviteReturn ? 'Back to the invite' : 'Go to TradeMagic Play'}
              </Link>
            </p>
          </>
        )}
      </section>
    </PlayLayout>
  );
};

export default DiscordCompletePage;
